
import React, { useState } from 'react';
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calculator as CalculatorIcon, ArrowRight, DollarSign, ChevronDown, ChevronUp } from "lucide-react";
import { ConsultationForm } from "@/components/forms/ConsultationForm";

export const Calculator = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [employeeCount, setEmployeeCount] = useState<number>(50);
  const [deviceCount, setDeviceCount] = useState<number>(100);
  const [industryType, setIndustryType] = useState<string>("general");
  const [hasLegacySystems, setHasLegacySystems] = useState<boolean>(false);
  const [serverCount, setServerCount] = useState<number>(2);
  const [hasAdvancedSecurity, setHasAdvancedSecurity] = useState<boolean>(false);
  const [calculatedValue, setCalculatedValue] = useState<number | null>(null);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState<boolean>(false);

  // Industry risk factors (multipliers for the base cost)
  const industryFactors = {
    general: 1.0,
    healthcare: 1.4,
    finance: 1.35,
    manufacturing: 1.1,
    retail: 1.15,
    legal: 1.25,
    nonprofit: 0.95,
    education: 1.0,
    technology: 1.2
  };

  const calculateValue = () => {
    // Base cost per employee
    const baseEmployeeCost = 65;
    
    // Base cost per device
    const baseDeviceCost = 32;
    
    // Base cost per server
    const baseServerCost = 300;
    
    // Additional cost for legacy systems
    const legacySystemsCost = hasLegacySystems ? 500 : 0;
    
    // Additional cost for advanced security
    const advancedSecurityCost = hasAdvancedSecurity ? 
      (employeeCount * 10 + deviceCount * 5) : 0;
    
    // Apply industry risk factor
    const industryFactor = industryFactors[industryType as keyof typeof industryFactors];
    
    // Calculate total monthly cost
    const totalMonthlyCost = (
      ((baseEmployeeCost * employeeCount) + 
      (baseDeviceCost * deviceCount) + 
      (baseServerCost * serverCount) + 
      legacySystemsCost + 
      advancedSecurityCost) * industryFactor
    );
    
    // Calculate estimated cost savings (typically 15-25% of traditional IT costs)
    const costSavings = totalMonthlyCost * 0.2;
    
    // Calculate productivity benefits (approximately $175 per employee per month)
    const productivityBenefits = employeeCount * 175;
    
    // Calculate risk reduction value (based on industry and size)
    const riskReductionValue = totalMonthlyCost * 0.15 * industryFactor;
    
    // Calculate total value
    const totalValue = costSavings + productivityBenefits + riskReductionValue;
    
    setCalculatedValue(Math.round(totalValue));
    setShowDetails(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const openResultModal = () => {
    setIsResultModalOpen(true);
  };

  return (
    <div className="w-full my-16" id="calculator">
      <div
        className="mb-8 flex items-center justify-between cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-edgefi-purple to-edgefi-purple/80">
          Estimate Your IT Value Potential
        </h2>
        <Button variant="outline" className="rounded-full">
          {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </Button>
      </div>

      {isOpen && (
        <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 transition-all duration-500 animate-fade-in">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="space-y-6">
              <div className="space-y-3">
                <Label htmlFor="employee-count" className="text-lg font-medium">
                  Number of Employees
                </Label>
                <div className="flex items-center gap-4">
                  <Slider
                    id="employee-count"
                    defaultValue={[50]}
                    max={500}
                    step={5}
                    className="flex-1"
                    onValueChange={(value) => setEmployeeCount(value[0])}
                  />
                  <Input
                    id="employee-count-input"
                    type="number"
                    value={employeeCount}
                    onChange={(e) => setEmployeeCount(Number(e.target.value))}
                    className="w-20"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label htmlFor="device-count" className="text-lg font-medium">
                  Number of Devices
                </Label>
                <div className="flex items-center gap-4">
                  <Slider
                    id="device-count"
                    defaultValue={[100]}
                    max={1000}
                    step={10}
                    className="flex-1"
                    onValueChange={(value) => setDeviceCount(value[0])}
                  />
                  <Input
                    id="device-count-input"
                    type="number"
                    value={deviceCount}
                    onChange={(e) => setDeviceCount(Number(e.target.value))}
                    className="w-20"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label htmlFor="server-count" className="text-lg font-medium">
                  Number of Servers
                </Label>
                <div className="flex items-center gap-4">
                  <Slider
                    id="server-count"
                    defaultValue={[2]}
                    max={20}
                    step={1}
                    className="flex-1"
                    onValueChange={(value) => setServerCount(value[0])}
                  />
                  <Input
                    id="server-count-input"
                    type="number"
                    value={serverCount}
                    onChange={(e) => setServerCount(Number(e.target.value))}
                    className="w-20"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="space-y-3">
                <Label htmlFor="industry-type" className="text-lg font-medium">
                  Industry
                </Label>
                <Select
                  defaultValue="general"
                  onValueChange={(value) => setIndustryType(value)}
                >
                  <SelectTrigger id="industry-type" className="w-full">
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General Business</SelectItem>
                    <SelectItem value="healthcare">Healthcare</SelectItem>
                    <SelectItem value="finance">Finance/Banking</SelectItem>
                    <SelectItem value="manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="legal">Legal</SelectItem>
                    <SelectItem value="nonprofit">Non-profit</SelectItem>
                    <SelectItem value="education">Education</SelectItem>
                    <SelectItem value="technology">Technology</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="legacy-systems" className="text-lg font-medium">
                    Do you have legacy systems?
                  </Label>
                  <Switch
                    id="legacy-systems"
                    checked={hasLegacySystems}
                    onCheckedChange={setHasLegacySystems}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="advanced-security" className="text-lg font-medium">
                    Need advanced security?
                  </Label>
                  <Switch
                    id="advanced-security"
                    checked={hasAdvancedSecurity}
                    onCheckedChange={setHasAdvancedSecurity}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-8">
            <Button 
              variant="cta" 
              size="lg" 
              onClick={calculateValue}
              className="flex items-center gap-2"
            >
              <CalculatorIcon className="h-5 w-5" />
              Calculate Value Potential
              <ArrowRight className="h-5 w-5" />
            </Button>
          </div>

          {calculatedValue !== null && (
            <div className="mt-10 p-8 bg-gradient-to-br from-edgefi-purple/10 to-edgefi-chartreuse/10 rounded-2xl animate-fade-in">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  Your Estimated Monthly Value
                </h3>
                <div className="text-4xl font-bold text-edgefi-purple flex items-center justify-center">
                  <DollarSign className="h-8 w-8" />
                  {formatCurrency(calculatedValue)}
                </div>
                <p className="text-gray-600 mt-2">
                  Potential monthly value from cost savings, productivity improvements, and risk reduction
                </p>
              </div>

              <div className="mt-4 flex justify-center">
                <Button 
                  variant="link" 
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center gap-1"
                >
                  {showDetails ? "Hide Details" : "Show Details"}
                  {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </div>

              {showDetails && (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 animate-fade-in">
                  <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                    <h4 className="font-semibold text-gray-800">Cost Savings</h4>
                    <p className="text-2xl font-bold text-edgefi-purple mt-2">
                      {formatCurrency(calculatedValue * 0.2)}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      From efficient management and reduced downtime
                    </p>
                  </div>
                  <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                    <h4 className="font-semibold text-gray-800">Productivity Benefits</h4>
                    <p className="text-2xl font-bold text-edgefi-amethyst mt-2">
                      {formatCurrency(employeeCount * 175)}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      From improved operations and reliable systems
                    </p>
                  </div>
                  <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                    <h4 className="font-semibold text-gray-800">Risk Reduction</h4>
                    <p className="text-2xl font-bold text-gray-700 mt-2">
                      {formatCurrency(calculatedValue * 0.15 * industryFactors[industryType as keyof typeof industryFactors])}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      From enhanced security and compliance
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-8 text-center">
                <Button
                  variant="cta"
                  size="lg"
                  className="group"
                  onClick={openResultModal}
                >
                  Talk to an Expert About Your Results
                  <ArrowRight className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Results Modal with Consultation Form */}
      <Dialog open={isResultModalOpen} onOpenChange={setIsResultModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-edgefi-purple">Discuss Your IT Value Potential</DialogTitle>
            <DialogDescription>
              Our experts can help you understand how to realize the estimated value of {calculatedValue ? formatCurrency(calculatedValue) : "$0"} per month for your business.
            </DialogDescription>
          </DialogHeader>
          
          <ConsultationFormWithResults calculatedValue={calculatedValue} industryType={industryType} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Custom component that extends ConsultationForm with hidden results fields
const ConsultationFormWithResults = ({ calculatedValue, industryType }: { calculatedValue: number | null, industryType: string }) => {
  return (
    <div>
      <div className="mb-6 p-4 bg-purple-50 rounded-lg">
        <p className="font-medium text-gray-700">Your Calculation Summary:</p>
        <p className="text-edgefi-purple font-bold text-lg">
          Estimated Monthly Value: {calculatedValue ? new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0
          }).format(calculatedValue) : "$0"}
        </p>
        <p className="text-sm text-gray-500">Industry: {industryType.charAt(0).toUpperCase() + industryType.slice(1)}</p>
      </div>
      
      <ConsultationForm calculationResult={calculatedValue} industryType={industryType} />
    </div>
  );
};
